import { computed, type ComputedRef, type Ref, unref } from 'vue'

import { useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'

import { renderMarkdownToHtml, substitute } from '@/composables/contentful'
import { formatWholeDollarCurrency } from '@/lib/utils'

import type { RewardTier } from '@/types/RewardTier'

/** Presentational props for the Free Gift callout, assembled from promotion + reward state. */
export interface FreeGiftCalloutProps {
  /**
   * Heading HTML, markdown emphasis (`**…**`) rendered.
   *
   * The authored text may use the tracker's own placeholders — `##AmountToNext##` and
   * `##NextRewardName##` — so "Spend **##AmountToNext## More** and Unlock ##NextRewardName##!" works
   * with no code change. `##amount##` is the legacy spelling of the first. See `headingVars`.
   */
  headingHtml: string
  /**
   * The reward's name — the granted package's name, or the promotion's retail name. May be empty.
   *
   * Was `lockedItemName`. Renamed at A2a because the callout now draws an unlocked state too and
   * shows this name in both, so the old name described the only state it used to have.
   */
  rewardName: string
  /** Resolved detail-image URL (left of the card), when authored. */
  detailImageUrl?: string
  /** The reward the **unlocked** half names, when a sequence is celebrating one — see below. */
  unlockedRewardName?: string
  /** That reward's detail image, for the same reason. */
  unlockedDetailImageUrl?: string
}

export interface UseFreeGiftOptions {
  /**
   * A subtotal to derive the **locked** half from instead of the cart's, while the unlock sequence
   * holds the banner.
   *
   * The banner has to hold its pre-crossing appearance until the 4,000ms card-advance beat, exactly as
   * the tracker's nodes and the reward rows do — otherwise it names the *next* locked reward from the
   * moment the cart response lands, and the celebration congratulates the customer beside a reward
   * they have not earned.
   *
   * It is a **subtotal rather than a pinned tier**, and that is the whole trick: a crossed tier's own
   * `amountToUnlock` is zero, so pinning the tier would render "Spend $0 More" and the amount would
   * need plumbing separately. Rewinding the subtotal makes the tier *and* its copy fall out of the
   * derivation that already exists. Null (the resting value) means "use the cart".
   */
  heldSubtotal?: Ref<number | null>
  /**
   * The tier the running sequence is celebrating, which the unlocked half names and pictures.
   *
   * It differs from the locked half's reward **on a multi-tier crossing, and only then**: the locked
   * frame is the reward the customer was working toward when they acted, and the unlocked frame is the
   * one they actually won — the *last* tier crossed, not the first. Two subjects, which is what a
   * crossfade is for.
   */
  celebratedTier?: Ref<RewardTier | null>
}

export interface UseFreeGift {
  /** The resolved callout props, or `null` when there is no locked gift with callout content to show. */
  callout: ComputedRef<FreeGiftCalloutProps | null>
}

/**
 * Cart-flyout Free Gift callout. "Callout" is our internal name — the business calls it a "banner", but
 * it isn't a full-width banner UI. Content-emergent: it shows whenever the next (`Current`) reward tier's
 * promotion has callout content authored (`catalog.freeGiftContent`) and that tier is still locked.
 * Display-only — no CTA. After a tier is crossed, `nextRewardTier` advances and this re-evaluates against
 * the following tier. Mirrors `useQuickAdds`.
 *
 * **The near-threshold rule is retired** (SUN-292 Gap 10). It previously also required the cart to be
 * within `freeGiftCalloutNearThreshold` dollars of unlocking, which meant an empty cart saw nothing —
 * exactly when the incentive is most useful. The board draws the callout on its empty-cart frames, so the
 * only remaining condition is "a locked gift with content authored". The Contentful field survives
 * unread, pending a disposition decision.
 *
 * **It no longer replaces YMAL either** — the two were `v-if`/`v-else` in `AppCartSidebar`, so a visible
 * callout suppressed Quick Adds entirely. They coexist as of S10, per the board.
 *
 * ---
 *
 * **It supplies two rewards, not one** (A2b). Called bare it describes the cart as it is, which is the
 * resting state and every non-flyout caller's need. Called with {@link UseFreeGiftOptions} it also
 * describes the crossing in flight, and the two halves of the crossfade are genuinely about different
 * rewards:
 *
 * | Half | Which reward | Which amount |
 * |---|---|---|
 * | Locked, until the card advances | the tier being worked toward **when the customer acted** | what it still needed from the pre-mutation subtotal |
 * | Unlocked, at the celebration | the tier **celebrated** — the last one crossed | none shown |
 * | Locked again, after the advance | the next locked tier, from the cart as it now is | its live `amountToUnlock` |
 *
 * Both wrong answers were tried in the harness first and both look reasonable in isolation: pin the
 * celebrated tier and the locked frame claims the customer was working toward a reward they had not
 * reached; rewind to the pre-mutation cart and the celebration congratulates them on the first tier
 * crossed rather than the last. The fix is not choosing between them — it is carrying both.
 */
export function useFreeGift(options: UseFreeGiftOptions = {}): UseFreeGift {
  const catalogStore = useCatalogStore()
  const cartStore = useCartStore()

  const effectiveSubtotal = computed(() => cartStore.effectiveSubtotal)
  /** The cart as the locked half should present it — rewound while the sequence holds the banner. */
  const lockedSubtotal = computed(() => unref(options.heldSubtotal) ?? effectiveSubtotal.value)
  const tier = computed(() => catalogStore.nextRewardTier(lockedSubtotal.value))
  const content = computed(() =>
    tier.value ? catalogStore.freeGiftContent(tier.value.promotionId) : undefined
  )

  // Reward name: the granted package's own retail name (`additionalBundleId` → catalog bundle
  // content `name`), falling back to the promotion's `retailName` when it grants no bundle.
  const rewardNameFor = (candidate: RewardTier | null): string => {
    if (!candidate) return ''
    if (candidate.additionalBundleId) {
      const grantedBundle = catalogStore.catalogBundlesWithContent.find(
        (cb) => cb.bundleId === candidate.additionalBundleId
      )
      if (grantedBundle?.content?.name) {
        return grantedBundle.content.name
      }
    }
    return catalogStore.promotionContent(candidate.promotionId)?.retailName ?? ''
  }

  const rewardName = computed<string>(() => rewardNameFor(tier.value))

  /**
   * Placeholders the authored heading can use — **the tracker's vocabulary, not a second one.**
   *
   * `buildRewardsHeading` has offered `##AmountToNext##` and `##NextRewardName##` since the rewards
   * expansion, and this callout sits directly beneath the tracker in the same panel. It previously took
   * only `##amount##`, which meant an author who reasonably copied the tracker's spelling got the
   * literal text `##AmountToNext##` rendered on the page — `substitute` preserves unmatched markers on
   * purpose, so the mistake is visible rather than silent, but it is still a mistake nobody should be
   * able to make between two fields on one screen.
   *
   * **Whole dollars, matching the tracker.** This used to render cents while the tracker floored to
   * whole dollars, so the same gap appeared twice in one panel as "$39" and "$39.00". One number, two
   * spellings, a few pixels apart. `formatWholeDollarCurrency` also carries a guard plain formatting
   * does not: an amount under a dollar rounds *up* to "$1", because "$0 more" reads as already
   * unlocked.
   *
   * `##amount##` is kept as the legacy spelling so existing authored entries keep working. It now
   * resolves to the same whole-dollar string as `##AmountToNext##` — deliberately, since leaving it on
   * the old formatting would preserve the very inconsistency this closes.
   *
   * `##NextRewardName##` is the **tracker's** label for the reward, which is what makes the shared
   * token honest: the same marker means the same thing in both fields. Note it can differ from the
   * name on the callout's own card, which comes from the granted bundle — see {@link rewardNameFor}.
   * That divergence is authored data (`rewardTrackerLabel` against the bundle's `name`) and reconciling
   * it is a content decision, not one this composable should make.
   */
  const headingVars = (t: RewardTier): Record<string, string> => {
    const amount = formatWholeDollarCurrency(t.amountToUnlock)
    return {
      AmountToNext: amount,
      NextRewardName: t.label,
      amount
    }
  }

  /** The celebrated tier's own name and image, or `undefined` so the callout falls back to the locked half. */
  const celebrated = computed(() => unref(options.celebratedTier) ?? null)

  const callout = computed<FreeGiftCalloutProps | null>(() => {
    const t = tier.value
    const c = content.value
    // Show whenever there's a locked tier with callout content authored. `amountToUnlock <= 0` means the
    // tier is already unlocked, so there is nothing left to spend toward and no incentive to show.
    if (!t || !c || t.amountToUnlock <= 0) {
      return null
    }

    const celebratedTier = celebrated.value

    return {
      headingHtml: renderMarkdownToHtml(substitute(c.heading, headingVars(t))),
      rewardName: rewardName.value,
      detailImageUrl: c.detailImage?.file?.url,
      // Left undefined with no sequence running, which is what makes the callout fall back to the
      // locked half — the single-tier case, where the two are the same reward anyway.
      unlockedRewardName: celebratedTier ? rewardNameFor(celebratedTier) : undefined,
      unlockedDetailImageUrl: celebratedTier
        ? catalogStore.freeGiftContent(celebratedTier.promotionId)?.detailImage?.file?.url
        : undefined
    }
  })

  return { callout }
}
